var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AssetCollection } from '../AssetCollection.js';
import { Material } from './Material.js';
import { ViewerMaterial } from './material/ViewerMaterial.js';
import { ViewerMaterialWrapper } from './material/ViewerMaterialWrapper.js';
import { Material as BABYLON_Material } from '@inter-ikea-betrakta-babylonjs/core/Materials/material.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { GLTF2Export as BABYLON_GLTF2Export } from '@inter-ikea-betrakta-babylonjs/serializers/glTF/2.0/glTFSerializer.js';
export class AssetManager {
    constructor(scene, freezing_manager, mark_scene_dirty, viewer) {
        this.scene = scene;
        this.freezing_manager = freezing_manager;
        this.mark_scene_dirty = mark_scene_dirty;
        this.viewer = viewer;
        this.materials = new AssetCollection();
        this.textures = new AssetCollection();
        this.lines = new Set();
        this.ui_meshes = new Set();
        this.text_fields = new Set();
        // Gizmos are UI meshes, but we need to store a list of Gizmos as well, for input.
        // This set should not be used for rendering, as the gizmos will be in the ui_meshes set as well, which is used for rendering.
        this.gizmos = new Set();
        this.compiling_material_promises = new Map();
        this._material_to_material_wrapper = new WeakMap();
        this.default_material = new ViewerMaterial('VIEWER_DEFAULT', this.viewer.scene, this.viewer);
        this.default_material.albedoColor.set(1, 0, 1); // Ultra magenta
        this.default_material.unlit = true;
        this.default_material.sideOrientation = BABYLON_Material.CounterClockWiseSideOrientation;
    }
    get_or_create_material_wrapper(material) {
        let material_wrapper = this._material_to_material_wrapper.get(material);
        if (material_wrapper === undefined) {
            material_wrapper = new ViewerMaterialWrapper(material, false, this, this.mark_scene_dirty);
            this._material_to_material_wrapper.set(material, material_wrapper);
        }
        return material_wrapper;
    }
    generate_file_name() {
        const d = new Date();
        // creates filename as export-dd-mm-yyy-hh_mm_ss
        return `export-${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}-${d.getHours()}_${d.getMinutes()}_${d.getSeconds()}`;
    }
    serialize_glb(filter, filename = this.generate_file_name(), center = false) {
        let export_translation = BABYLON_Vector3.Zero();
        const babylon_root_obj_ids = new Set();
        return Promise.all(filter.map(scene_object => scene_object.ready))
            .then(() => {
            const babylon_ids = new Set();
            // For each object, find all it's meshes and their hierarchy
            filter.forEach(object => {
                object.mesh_iterator(false, mesh => {
                    babylon_ids.add(mesh.uniqueId);
                    // Now go get its ancestors
                    let current = mesh;
                    while (current.parent && !babylon_ids.has(current.parent.uniqueId)) {
                        babylon_ids.add(current.parent.uniqueId);
                        current = current.parent;
                    }
                });
            });
            if (center) {
                filter.forEach(object => {
                    let node = object;
                    while (node.parent) {
                        node = node.parent;
                    }
                    babylon_root_obj_ids.add(node.id);
                });
                // Compute the AABB of the object set
                let min_world = new BABYLON_Vector3(Infinity, Infinity, Infinity);
                let max_world = new BABYLON_Vector3(-Infinity, -Infinity, -Infinity);
                filter.forEach(object => {
                    object.mesh_iterator(true, mesh => {
                        const bb = mesh.getBoundingInfo().boundingBox;
                        min_world = new BABYLON_Vector3(Math.min(min_world.x, bb.minimumWorld.x), Math.min(min_world.y, bb.minimumWorld.y), Math.min(min_world.z, bb.minimumWorld.z));
                        max_world = new BABYLON_Vector3(Math.max(max_world.x, bb.maximumWorld.x), Math.max(max_world.y, bb.maximumWorld.y), Math.max(max_world.z, bb.maximumWorld.z));
                    });
                });
                const center_world = min_world.add(max_world).scale(0.5);
                export_translation = center_world.negate(); // Applying this translation to all objects will move the ceneter point to the global origin.
                // Apply export translation
                babylon_root_obj_ids.forEach(object_id => {
                    const object = this.viewer.viewer_scene.objects.get(object_id);
                    const pos = object.get_position().clone().add(export_translation);
                    object.set_position(pos.x, pos.y, pos.z);
                });
            }
            // Disable viewer linting, follow BABYLON linting rules
            const options = {
                // eslint-disable-next-line
                shouldExportNode: (babylon_node) => babylon_ids.has(babylon_node.uniqueId)
            };
            return BABYLON_GLTF2Export.GLBAsync(this.scene, filename, options);
        })
            .then(gltf => {
            // Revert export translation
            if (center) {
                babylon_root_obj_ids.forEach(object_id => {
                    const object = this.viewer.viewer_scene.objects.get(object_id);
                    const pos = object.get_position().clone().subtract(export_translation);
                    object.set_position(pos.x, pos.y, pos.z);
                });
            }
            return gltf.glTFFiles;
        });
    }
    create_material(name) {
        const new_material = new ViewerMaterial(`${name !== null && name !== void 0 ? name : 'material'}_${this.materials.length()}`, this.scene, this.viewer);
        new_material.sideOrientation = BABYLON_Material.CounterClockWiseSideOrientation;
        new_material.useLightmapAsShadowmap = true; // Lightmaps in viewer are always multiplicative
        new_material.roughness = 1.0;
        new_material.metallic = 1.0;
        return this.add_material_from_babylon(new_material);
    }
    add_material(v) {
        return this.materials.add(v || new Material(this.materials.next_id(), this.textures, this.freezing_manager));
    }
    add_material_from_babylon(v) {
        return this.materials.add(new Material(this.materials.next_id(), this.textures, this.freezing_manager, v));
    }
    /**
     * Returns true if any of the given nodes parents have any of the supplied names, false otherwise
     */
    has_parent_with_name(node, names) {
        let parent = node.parent;
        while (parent !== null) {
            for (let i = 0; i < names.length; i++) {
                const name = names[i];
                if (parent.name === name) {
                    return true;
                }
            }
            parent = parent.parent;
        }
        return false;
    }
    /**
     * Checks if a given material is assigned to a mesh that is not used as a template.
     * @param material_id material to check
     * @returns true if the material is assigned to a mesh that is not used as a template
     */
    is_material_used(material_id) {
        var _a;
        const material = this.materials.get(material_id);
        const m = (_a = material.material) !== null && _a !== void 0 ? _a : material.multi_material;
        if (m !== null) {
            for (const key in m.meshMap) {
                const mesh = m.meshMap[key];
                if (mesh !== undefined) {
                    if (mesh.isEnabled(true)) {
                        return true;
                    }
                    else if (!this.has_parent_with_name(mesh, ['materials-templates', 'model-templates', 'source-meshes'])) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    delete_material(material_id) {
        if (!this.materials.contains(material_id)) {
            console.warn(`Not able to remove material with id: ${material_id}. The material ID provided does not exist.`);
            return false;
        }
        const material = this.materials.get(material_id);
        if (material.is_default_created_material) {
            console.error('Default materials will be deleted when their object is deleted. Do not call delete_material');
            return false;
        }
        if (material._material_url !== null) {
            console.error('Materials loaded from a gltf can not be deleted using delete_material, use unload_models instead');
            return false;
        }
        if (this.is_material_used(material_id)) {
            console.warn('delete_material; The material is in use!');
        }
        material.dispose();
        return this.materials.remove(material_id);
    }
    delete_material_if_default_created(material) {
        // NOTE: Shared materials are more or less assumed to be ready at construction
        if (material === null || !this.materials.contains(material)) {
            return;
        }
        const babylon_material = this.materials.get(material);
        if (!babylon_material.is_default_created_material) {
            return;
        }
        babylon_material.ready.then(() => {
            if (!this.materials.contains(material)) {
                return;
            }
            babylon_material.dispose();
            this.materials.remove(material);
        }, () => {
            console.warn('Babylon material is not ready to be disposed.');
        });
    }
    register_scene_lines(lines) {
        lines.disposed_observable.add_once(() => {
            this.lines.delete(lines);
        });
        this.lines.add(lines);
    }
    add_texture(v) {
        return this.textures.add(v);
    }
    dispose() {
        this.materials.dispose();
        this.textures.dispose();
        this.lines.forEach((line) => line.dispose());
        this.lines.clear();
    }
}
function texture_to_canvas(texture, flip_canvas) {
    return __awaiter(this, void 0, void 0, function* () {
        const pixels = yield texture.readPixels();
        const size = texture.getSize();
        const width = size.width;
        const height = size.height;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        const image_data = ctx.createImageData(width, height);
        if (pixels instanceof Float32Array) {
            for (let i = 0; i < image_data.data.length; i++) {
                image_data.data[i] = Math.trunc(pixels[i] * 255);
            }
        }
        else {
            for (let i = 0; i < image_data.data.length; i++) {
                image_data.data[i] = pixels[i];
            }
        }
        ctx.putImageData(image_data, 0, 0);
        if (flip_canvas) {
            const flipped_canvas = document.createElement('canvas');
            flipped_canvas.width = width;
            flipped_canvas.height = height;
            const flipped_canvas_ctx = flipped_canvas.getContext('2d');
            flipped_canvas_ctx.save(); // Save the current state
            flipped_canvas_ctx.translate(0, height);
            flipped_canvas_ctx.scale(1.0, -1.0); // Set scale to flip the image
            flipped_canvas_ctx.drawImage(canvas, 0, 0, width, height); // draw the image
            flipped_canvas_ctx.restore(); // Restore the last saved state
            return flipped_canvas;
        }
        else {
            return canvas;
        }
    });
}
export function debug_download_texture(texture) {
    return __awaiter(this, void 0, void 0, function* () {
        const canvas = yield texture_to_canvas(texture, false);
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = `${texture.name || 'texture'}.png`;
        link.click();
        canvas.remove();
    });
}
export function texture_to_base64(texture_1) {
    return __awaiter(this, arguments, void 0, function* (texture, mime_type = 'image/png', image_compression = 1.0) {
        const canvas = yield texture_to_canvas(texture, true);
        return canvas.toDataURL(mime_type, image_compression);
    });
}
export function download_gltf_files(files) {
    function ends_with(str, suffix) {
        return str.indexOf(suffix, str.length - suffix.length) !== -1;
    }
    for (const key in files) {
        const link = document.createElement('a');
        document.body.appendChild(link);
        link.setAttribute('type', 'hidden');
        link.download = key;
        const blob = files[key];
        let mime_type;
        if (ends_with(key, '.glb')) {
            mime_type = { type: 'model/gltf-binary' };
        }
        else if (ends_with(key, '.bin')) {
            mime_type = { type: 'application/octet-stream' };
        }
        else if (ends_with(key, '.gltf')) {
            mime_type = { type: 'model/gltf+json' };
        }
        else if (ends_with(key, '.jpeg') || ends_with(key, '.jpg')) {
            mime_type = { type: 'image/jpeg' };
        }
        else if (ends_with(key, '.png')) {
            mime_type = { type: 'image/png' };
        }
        link.href = window.URL.createObjectURL(new Blob([blob], mime_type));
        link.click();
    }
}
//# sourceMappingURL=AssetManager.js.map